import { useQuery } from "@tanstack/react-query"
import { CheckIcon, ChevronsUpDownIcon, XIcon } from "lucide-react"
import { useState } from "react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@atlant/ui"

import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { getUserImageUrl } from "#utils/file-url.ts"
import { client } from "#utils/graphql-client.ts"

const OrganizationUsersDocument = graphql(/* GraphQL */ `
  query OrganizationUsers {
    users {
      id
      name
      image
      role
    }
  }
`)

const initials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

interface FormUsersSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  name?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: string
}

export const FormUsersSelect = ({
  value,
  onChange,
  name,
  label,
  placeholder = "Select users",
  disabled,
  className,
  error,
}: FormUsersSelectProps) => {
  const [open, setOpen] = useState(false)
  const {
    me: { organization },
  } = useLoggedInUserInfo()

  const { data } = useQuery({
    queryKey: queryKeys.users.list(organization.id),
    queryFn: () => client.request(OrganizationUsersDocument),
  })
  const users = data?.users ?? []
  const selected = users.filter((u) => value.includes(u.id))

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])

  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel htmlFor={id} className="text-muted-foreground text-sm font-medium">
          {label}
        </FieldLabel>
      )}
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            type="button"
            aria-invalid={error ? true : undefined}
            className={cn(
              "border-input hover:bg-input/10 h-auto min-h-10 w-full justify-between gap-2 px-2 py-1.5 text-left font-normal",
              error && "border-destructive dark:border-destructive",
            )}
          >
            {selected.length > 0 ? (
              <span className="flex flex-wrap gap-1">
                {selected.map((user) => (
                  <Badge key={user.id} variant="secondary" className="gap-1 pr-1 pl-0.5">
                    <Avatar className="size-4">
                      <AvatarImage src={getUserImageUrl(organization.id, user.image)} />
                      <AvatarFallback className="text-[8px]">
                        {initials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    {user.name}
                    <span
                      role="button"
                      aria-label={`Remove ${user.name}`}
                      className="hover:bg-foreground/10 rounded-sm"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggle(user.id)
                      }}
                    >
                      <XIcon className="size-3" />
                    </span>
                  </Badge>
                ))}
              </span>
            ) : (
              <span className="text-placeholder px-1">{placeholder}</span>
            )}
            <ChevronsUpDownIcon
              size={16}
              className="text-muted-foreground/80 shrink-0"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="border-input w-(--radix-popper-anchor-width) p-1"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search user..." />
            <CommandList>
              <CommandEmpty>No users found</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${user.name} ${user.role}`}
                    onSelect={() => toggle(user.id)}
                    className="gap-2"
                  >
                    <Avatar className="size-6">
                      <AvatarImage src={getUserImageUrl(organization.id, user.image)} />
                      <AvatarFallback className="text-[10px]">
                        {initials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1 truncate">
                      {user.name}{" "}
                      {user.role.length !== 0 && (
                        <span className="text-muted-foreground">({user.role})</span>
                      )}
                    </span>
                    {value.includes(user.id) && (
                      <CheckIcon size={16} className="ml-auto" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
